import {
  getAuthTokenBlocking,
  HealthEvent,
  RecentHealthEvent,
  ReloginContext,
  updateHealthEventHttp,
  useHealthEventsInRange,
  useHealthOverview,
  useOperationalDeploymentStats,
} from "@inrange/building-manager-api-client";
import { SiteElectricalSystem } from "@inrange/building-manager-api-client/models-site";
import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AvailabilityBreakdownPanel from "./AvailabilityBreakdownPanel";
import PRBreakdownPanel from "./PRBreakdownPanel";
import SiteBreakdownPanel from "./SiteBreakdownPanel";
import { AVAILABILITY_TILES, RYG_ICONS } from "./deploymentStatsUtils";
import { buildMonthFilterOptions } from "./site/healthDateFilters";
import { HealthEventsTable } from "./site/SiteHealthStatus";

// The cross-site Health Events table has no single site timezone; the digest
// and other operational surfaces present UK-local, so we render with London.
const HEALTH_TZ = "Europe/London";

/**
 * Period-only notice for the default "Current and previous month" filter:
 * the window reaches back to the first of the previous calendar month in UTC,
 * matching the `range` computed for that filter. Stays generic about which
 * events are shown - the operator-selected status filter narrows further, so
 * baking event-type language into the notice would clash with the filter
 * buttons.
 */
const buildOperationalHealthDataRangeNotice = (now: Date): string => {
  const previousMonthStart = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
  );
  const cutoff = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(previousMonthStart);
  return `Since ${cutoff} (start of last month).`;
};

interface StatTileProps {
  value: string;
  label: string;
  rygColor?: string;
  onClick?: () => void;
}

const StatTile = ({ value, label, rygColor, onClick }: StatTileProps) => {
  const icon = rygColor ? RYG_ICONS[rygColor] : undefined;
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #212529",
        borderRadius: "12px",
        padding: "16px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "160px",
        width: "160px",
        flexShrink: 0,
        cursor: onClick ? "pointer" : undefined,
      }}
      onClick={onClick}
    >
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 700,
          color: "#212529",
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {icon && <span style={{ fontSize: "1rem" }}>{icon} </span>}
        {value}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          color: "#6c757d",
          marginTop: "6px",
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {label}
      </div>
    </div>
  );
};

// Loading placeholder confined to the top tiles row while the heavy
// availability/PR overview LRO computes. The events table below renders
// independently from the fast range fetch and is not blocked by this.
const HealthTilesLoading = ({
  progress,
}: {
  progress?: { current: number; total: number; message: string };
}) => (
  <div className="mb-3 text-center">
    <Spinner animation="border" size="sm" /> Loading health overview...
    {progress && (
      <div style={{ marginTop: "12px" }}>
        <div style={{ color: "#6c757d", marginBottom: "8px" }}>
          {progress.message}
        </div>
        <div
          style={{
            width: "300px",
            height: "6px",
            backgroundColor: "#e9ecef",
            borderRadius: "3px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              width: `${(progress.current / progress.total) * 100}%`,
              height: "100%",
              backgroundColor: "#212529",
              borderRadius: "3px",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>
    )}
  </div>
);

const PV_AVAILABILITY_TILES = AVAILABILITY_TILES.filter(
  ({ source }) => source === "aggregateAvailability"
);

const OperationalHealth = () => {
  const healthOverview = useHealthOverview();
  const { fetchDeploymentStats } = useOperationalDeploymentStats();
  const [activePanel, setActivePanel] = useState<string | null>(null);

  // Date filter for the Health Events table. The events table is driven entirely
  // by the lightweight cross-site GET /health/events range fetch (NOT the heavy
  // availability/PR overview LRO), so events render quickly and independently of
  // the top tiles. The filter values are:
  //   - "currentAndPrev" — default; current + previous calendar month.
  //   - "30"             — last 30 days.
  //   - "YYYY-MM"        — a specific calendar month (reaches arbitrary history).
  const [dateFilter, setDateFilter] = useState<string>("currentAndPrev");
  const monthOptions = useMemo(
    () => buildMonthFilterOptions(new Date(), HEALTH_TZ),
    []
  );

  const range = useMemo((): { from: string; to: string } => {
    const now = new Date();
    if (/^\d{4}-\d{2}$/.test(dateFilter)) {
      const [year, month] = dateFilter.split("-").map(Number);
      return {
        from: new Date(Date.UTC(year, month - 1, 1)).toISOString(),
        to: new Date(Date.UTC(year, month, 1) - 1).toISOString(),
      };
    }
    if (dateFilter === "30") {
      return {
        from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        to: now.toISOString(),
      };
    }
    // "currentAndPrev" (default): start of the previous calendar month → now.
    return {
      from: new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
      ).toISOString(),
      to: now.toISOString(),
    };
  }, [dateFilter]);

  const rangeQuery = useHealthEventsInRange(range.from, range.to);

  const deploymentSites = useMemo(
    () => fetchDeploymentStats.data?.sites ?? [],
    [fetchDeploymentStats.data]
  );

  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const updateEvent = useMutation(
    async ({
      siteId,
      eventId,
      ...update
    }: {
      siteId: string;
      eventId: number;
      impactType?: string;
      notes?: string;
    }) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return updateHealthEventHttp(token, siteId, eventId, update);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["healthOverview"]);
        queryClient.invalidateQueries(["healthEventsInRange"]);
      },
    }
  );

  // The heavy availability/PR overview LRO only gates the top tiles + breakdown
  // panels; the events table renders independently from the fast range fetch.
  const data = healthOverview.data;

  const displayedEvents: RecentHealthEvent[] = rangeQuery.data?.events ?? [];

  let dataRangeNotice = buildOperationalHealthDataRangeNotice(new Date());
  if (dateFilter === "30") {
    dataRangeNotice = "Showing events active in the last 30 days.";
  } else if (/^\d{4}-\d{2}$/.test(dateFilter)) {
    const label =
      monthOptions.find((o) => o.key === dateFilter)?.label ?? dateFilter;
    dataRangeNotice = `Showing events active in ${label}.`;
  }

  const deploymentHealth = fetchDeploymentStats.data?.health;
  const currentIssueCount = deploymentHealth?.currentIssues ?? 0;
  const currentlyHealthyCount = deploymentHealth?.currentlyHealthy ?? 0;
  const lastMonthIssueCount = deploymentHealth?.lastMonthIssues ?? 0;
  const lastMonthHealthyCount = deploymentHealth?.lastMonthHealthy ?? 0;

  // Build siteNameByEvent map for the table
  const siteNameByEvent: Record<string, string> = {};
  for (const event of displayedEvents) {
    siteNameByEvent[String(event.id)] = event.siteName;
  }

  const handleUpdateImpactType = (eventId: number, impactType: string) => {
    const event = displayedEvents.find((e) => e.id === eventId);
    if (!event) return;
    updateEvent.mutate(
      { siteId: event.siteId, eventId, impactType },
      {
        onError: (err) => {
          alert(
            `Failed to update: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const handleUpdateNotes = (eventId: number, notes: string) => {
    const event = displayedEvents.find((e) => e.id === eventId);
    if (!event) return;
    updateEvent.mutate(
      { siteId: event.siteId, eventId, notes },
      {
        onError: (err) => {
          alert(
            `Failed to save notes: ${err instanceof Error ? err.message : "Unknown error"}`
          );
        },
      }
    );
  };

  const hasPanel = activePanel !== null;

  return (
    <div
      className="mt-3"
      style={hasPanel ? { paddingBottom: "52vh" } : undefined}
    >
      {/* Top tiles + breakdown panels: gated on the heavy overview LRO so the
          loading / error state stays confined here. The events table below
          renders independently from the fast range fetch. */}
      {healthOverview.isLoading && (
        <HealthTilesLoading progress={healthOverview.progress} />
      )}
      {healthOverview.isError && (
        <div className="text-danger mb-3">Failed to load health overview</div>
      )}
      {data && (
        <>
          {/* Summary tiles: 6 across (Availability, GPR, OPR, 4 health) */}
          <div className="d-flex gap-3 mb-3 flex-wrap">
            {PV_AVAILABILITY_TILES.map(({ key, label, excused }) => {
              const figures = data.aggregateAvailability;
              const pct = excused ? figures?.pct : figures?.allCausePct;
              return (
                <StatTile
                  key={key}
                  value={pct != null ? `${pct}%` : "N/A"}
                  label={label}
                  rygColor={excused ? figures?.ryg : figures?.allCauseRyg}
                  onClick={() =>
                    setActivePanel(activePanel === key ? null : key)
                  }
                />
              );
            })}
            <StatTile
              value={
                data.aggregatePR?.last12Months?.pct != null
                  ? `${data.aggregatePR.last12Months.pct}%`
                  : "N/A"
              }
              label="Aggregate GPR (excluded)"
              rygColor={data.aggregatePR?.last12Months?.ryg ?? undefined}
              onClick={() =>
                setActivePanel(activePanel === "gpr" ? null : "gpr")
              }
            />
            <StatTile
              value={
                data.aggregateOPR?.last12Months?.pct != null
                  ? `${data.aggregateOPR.last12Months.pct}%`
                  : "N/A"
              }
              label="Aggregate OPR (excluded)"
              rygColor={data.aggregateOPR?.last12Months?.ryg ?? undefined}
              onClick={() =>
                setActivePanel(activePanel === "opr" ? null : "opr")
              }
            />
            <StatTile
              value={String(currentIssueCount)}
              label="Current health issues"
              rygColor={currentIssueCount > 0 ? "red" : undefined}
              onClick={() =>
                setActivePanel(
                  activePanel === "currentIssues" ? null : "currentIssues"
                )
              }
            />
            <StatTile
              value={String(currentlyHealthyCount)}
              label="Currently healthy"
              rygColor={currentlyHealthyCount > 0 ? "green" : undefined}
              onClick={() =>
                setActivePanel(
                  activePanel === "currentlyHealthy" ? null : "currentlyHealthy"
                )
              }
            />
            <StatTile
              value={String(lastMonthIssueCount)}
              label="Last month analysis health issues"
              rygColor={lastMonthIssueCount > 0 ? "red" : undefined}
              onClick={() =>
                setActivePanel(
                  activePanel === "lastMonthIssues" ? null : "lastMonthIssues"
                )
              }
            />
            <StatTile
              value={String(lastMonthHealthyCount)}
              label="Last month analysis healthy"
              rygColor={lastMonthHealthyCount > 0 ? "green" : undefined}
              onClick={() =>
                setActivePanel(
                  activePanel === "lastMonthHealthy" ? null : "lastMonthHealthy"
                )
              }
            />
          </div>

          {/* Breakdown panels */}
          {PV_AVAILABILITY_TILES.filter(({ key }) => key === activePanel).map(
            ({ key, label, excused }) => (
              <AvailabilityBreakdownPanel
                key={key}
                sites={data.allSiteAvailability}
                aggregatePct={
                  (excused
                    ? data.aggregateAvailability?.pct
                    : data.aggregateAvailability?.allCausePct) ?? null
                }
                aggregateRyg={
                  (excused
                    ? data.aggregateAvailability?.ryg
                    : data.aggregateAvailability?.allCauseRyg) ?? "green"
                }
                onClose={() => setActivePanel(null)}
                metricLabel={label.replace("Aggregate ", "")}
                excused={excused}
              />
            )
          )}
          {activePanel === "gpr" && data.aggregatePR && data.allSitePR && (
            <PRBreakdownPanel
              sites={data.allSitePR}
              aggregatePR={data.aggregatePR}
              onClose={() => setActivePanel(null)}
            />
          )}
          {activePanel === "opr" && data.aggregateOPR && data.allSitePR && (
            <PRBreakdownPanel
              sites={data.allSitePR}
              aggregatePR={data.aggregateOPR}
              onClose={() => setActivePanel(null)}
              mode="opr"
            />
          )}
          {(activePanel === "currentIssues" ||
            activePanel === "currentlyHealthy" ||
            activePanel === "lastMonthIssues" ||
            activePanel === "lastMonthHealthy") && (
            <SiteBreakdownPanel
              metricKey={activePanel}
              sites={deploymentSites}
              currencyCode="GBP"
              onClose={() => setActivePanel(null)}
              tileLabel={
                {
                  currentIssues: "Current health issues",
                  currentlyHealthy: "Currently healthy",
                  lastMonthIssues: "Last month analysis health issues",
                  lastMonthHealthy: "Last month analysis healthy",
                }[activePanel as string] ?? "Health"
              }
            />
          )}
        </>
      )}

      {/* Events table — driven by the fast range fetch, so its loading state is
          confined to the card body (bodyOverride) and never blocks on the heavy
          overview LRO above. The period selector renders inside the card header
          (dateFilterControls), matching the per-site health page. */}
      <HealthEventsTable
        events={displayedEvents as HealthEvent[]}
        siteId=""
        timeZone={HEALTH_TZ}
        esysEntries={[] as [string, SiteElectricalSystem][]}
        onUpdateImpactType={handleUpdateImpactType}
        onUpdateNotes={handleUpdateNotes}
        onDeleteEvent={() => {}}
        isUpdating={updateEvent.isLoading}
        title="Health Events"
        showDateFilter={false}
        defaultStatusFilter="openAndNonTransientResolved"
        showSiteColumn={true}
        siteNameByEvent={siteNameByEvent}
        showDeleteColumn={false}
        dataRangeNotice={dataRangeNotice}
        dateFilterControls={
          <div className="d-flex gap-1 align-items-center flex-wrap">
            <Button
              size="sm"
              variant={dateFilter === "30" ? "dark" : "outline-secondary"}
              onClick={() => setDateFilter("30")}
            >
              Last 30 days
            </Button>
            <Button
              size="sm"
              variant={
                dateFilter === "currentAndPrev" ? "dark" : "outline-secondary"
              }
              onClick={() => setDateFilter("currentAndPrev")}
            >
              Current and previous month
            </Button>
            <Form.Select
              size="sm"
              style={{ width: "auto" }}
              value={/^\d{4}-\d{2}$/.test(dateFilter) ? dateFilter : ""}
              onChange={(e) =>
                setDateFilter(e.target.value || "currentAndPrev")
              }
            >
              <option value="">Specific month…</option>
              {monthOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </div>
        }
        bodyOverride={
          rangeQuery.isError ? (
            <div className="text-danger py-3 text-center">
              Failed to load health events
            </div>
          ) : rangeQuery.isLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading events…
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

export default OperationalHealth;
